(function () {
  const COOKIE_NAME = 'shopify_recently_viewed'
  const STORAGE_NAME = 'shopify_recently_viewed_products'
  const DEFAULT_LIMIT = 4
  const DEFAULT_EXPIRE_DAYS = 90

  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, char => htmlEscapes[char])
  }

  function parseHandles(value) {
    if (!value) return []

    let decodedValue = String(value)

    try {
      decodedValue = decodeURIComponent(decodedValue)
    } catch (error) {
      decodedValue = decodedValue.replace(/%20/gi, ' ')
    }

    const seen = new Set()

    return decodedValue
      .split(/[\s,|]+/)
      .map(handle => handle.trim())
      .filter(Boolean)
      .filter(handle => {
        if (seen.has(handle)) return false

        seen.add(handle)
        return true
      })
  }

  function serializeHandles(handles) {
    return handles.join(' ')
  }

  function addHandle(handles, handle, limit) {
    const cleanHandle = String(handle || '').trim()

    if (!cleanHandle || handles.includes(cleanHandle)) {
      return handles.slice(0, limit)
    }

    return [cleanHandle, ...handles].slice(0, limit)
  }

  function readCookie(name) {
    const parts = document.cookie ? document.cookie.split(';') : []
    const values = []

    for (const part of parts) {
      const trimmed = part.trim()
      if (trimmed.startsWith(`${name}=`)) {
        values.push(trimmed.slice(name.length + 1))
      }
    }

    return values.join(' ')
  }

  function writeCookie(name, value, expireDays) {
    const expires = new Date()
    expires.setTime(expires.getTime() + expireDays * 24 * 60 * 60 * 1000)
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/`
  }

  function normalizeProductData(product) {
    if (!product || typeof product !== 'object') return null

    const handle = String(product.handle || '').trim()
    const title = String(product.title || '').trim()

    if (!handle || !title) return null

    return {
      handle,
      title,
      price: String(product.price || '').trim(),
      url: String(product.url || `/products/${encodeURIComponent(handle)}`).trim(),
      image: String(product.image || '').trim(),
      imageAlt: String(product.imageAlt || title).trim(),
    }
  }

  function parseProductJson(value) {
    try {
      return normalizeProductData(JSON.parse(value || '{}'))
    } catch (error) {
      return null
    }
  }

  function readCurrentProductData(root) {
    const script =
      root.querySelector('[data-rv-current-product]') ||
      document.querySelector('[data-rv-current-product]')

    if (!script) return null

    return parseProductJson(script.textContent)
  }

  function readStoredProducts() {
    try {
      const parsed = JSON.parse(window.localStorage?.getItem(STORAGE_NAME) || '{}')
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}

      return parsed
    } catch (error) {
      return {}
    }
  }

  function writeStoredProducts(productsByHandle) {
    try {
      window.localStorage?.setItem(STORAGE_NAME, JSON.stringify(productsByHandle))
    } catch (error) {
      // Ignore unavailable storage; the persistent icon should still render.
    }
  }

  function upsertProductData(productsByHandle, product) {
    const normalized = normalizeProductData(product)
    if (!normalized) return { ...productsByHandle }

    return {
      ...productsByHandle,
      [normalized.handle]: normalized,
    }
  }

  function trimStoredProducts(productsByHandle, handles) {
    const trimmed = {}

    for (const handle of handles) {
      if (productsByHandle[handle]) {
        trimmed[handle] = productsByHandle[handle]
      }
    }

    return trimmed
  }

  function selectProductsByHandles(handles, productsByHandle) {
    return handles
      .map(handle => productsByHandle[handle])
      .filter(Boolean)
  }

  function renderProductItem(product) {
    const normalized = normalizeProductData(product)
    if (!normalized) return ''

    const title = escapeHtml(normalized.title)
    const price = escapeHtml(normalized.price)
    const image = escapeHtml(normalized.image)
    const imageAlt = escapeHtml(normalized.imageAlt)
    const url = escapeHtml(normalized.url)

    const media = normalized.image
      ? `<img class="rv-sidebar__image" src="${image}" alt="${imageAlt}" width="64" height="86" loading="lazy">`
      : `<span class="rv-sidebar__placeholder">${title}</span>`

    return [
      `<a class="rv-sidebar__item" href="${url}" data-rv-item data-rv-handle="${escapeHtml(normalized.handle)}" data-rv-title="${title}" data-rv-price="${price}" data-rv-image="${image}" data-rv-image-alt="${imageAlt}">`,
      media,
      '</a>',
    ].join('')
  }

  function setPanelContent(panel, item) {
    if (!panel || !item) return

    const title = item.getAttribute('data-rv-title') || ''
    const price = item.getAttribute('data-rv-price') || ''
    const image = item.getAttribute('data-rv-image') || ''
    const imageAlt = item.getAttribute('data-rv-image-alt') || title
    const url = item.getAttribute('href') || '#'

    panel.replaceChildren()

    const link = document.createElement('a')
    link.className = 'rv-sidebar__panel-link'
    link.href = url

    const text = document.createElement('span')
    text.className = 'rv-sidebar__panel-text'

    const titleElement = document.createElement('span')
    titleElement.className = 'rv-sidebar__panel-title'
    titleElement.textContent = title

    const priceElement = document.createElement('span')
    priceElement.className = 'rv-sidebar__panel-price'
    priceElement.textContent = price

    text.append(titleElement, priceElement)
    link.append(text)

    if (image) {
      const imageElement = document.createElement('img')
      imageElement.className = 'rv-sidebar__panel-image'
      imageElement.src = image
      imageElement.alt = imageAlt
      imageElement.width = 64
      imageElement.height = 86
      link.append(imageElement)
    }

    panel.append(link)
  }

  class RecentlyViewedSidebar extends HTMLElement {
    connectedCallback() {
      this.limit = Number(this.dataset.limit || DEFAULT_LIMIT)
      this.expireDays = Number(this.dataset.expireDays || DEFAULT_EXPIRE_DAYS)
      this.list = this.querySelector('[data-rv-list]')
      this.panel = this.querySelector('[data-rv-panel]')
      this.empty = this.querySelector('[data-rv-empty]')
      this.prevButton = this.querySelector('[data-rv-prev]')
      this.nextButton = this.querySelector('[data-rv-next]')
      this.toggleButton = this.querySelector('[data-rv-toggle]')
      this.scrollIndex = 0

      this.classList.add('is-ready')
      this.recordCurrentProduct()
      this.renderProducts()
      this.bindEvents()
    }

    recordCurrentProduct() {
      const currentProduct = readCurrentProductData(this)
      if (!currentProduct) return

      const handles = parseHandles(readCookie(COOKIE_NAME))
      const updatedHandles = addHandle(handles, currentProduct.handle, this.limit)
      const storedProducts = upsertProductData(readStoredProducts(), currentProduct)
      const trimmedProducts = trimStoredProducts(storedProducts, updatedHandles)

      writeStoredProducts(trimmedProducts)

      if (updatedHandles.join(' ') !== handles.slice(0, this.limit).join(' ')) {
        writeCookie(COOKIE_NAME, serializeHandles(updatedHandles), this.expireDays)
      }
    }

    renderProducts() {
      if (!this.list) return

      const handles = parseHandles(readCookie(COOKIE_NAME)).slice(0, this.limit)
      const products = selectProductsByHandles(handles, readStoredProducts())
        .map(normalizeProductData)
        .filter(Boolean)

      this.list.innerHTML = products.map(renderProductItem).join('')
      this.classList.toggle('is-empty', products.length === 0)

      if (this.empty) {
        this.empty.hidden = products.length !== 0
      }

      this.scrollIndex = 0
      this.updateButtons()
    }

    bindEvents() {
      this.toggleButton?.addEventListener('click', () => {
        const isExpanded = !this.classList.contains('is-expanded')

        this.classList.toggle('is-expanded', isExpanded)
        this.classList.remove('is-panel-visible')
        this.toggleButton.setAttribute('aria-expanded', String(isExpanded))
      })

      if (!this.list) return

      this.list.addEventListener('pointerenter', event => {
        const item = event.target.closest('[data-rv-item]')
        if (!item || !this.contains(item)) return

        setPanelContent(this.panel, item)
        this.classList.add('is-panel-visible')
      }, true)

      this.list.addEventListener('pointerleave', event => {
        const item = event.target.closest('[data-rv-item]')
        if (!item || !this.contains(item)) return

        this.classList.remove('is-panel-visible')
      }, true)

      this.list.addEventListener('focusin', event => {
        const item = event.target.closest('[data-rv-item]')
        if (!item || !this.contains(item)) return

        setPanelContent(this.panel, item)
        this.classList.add('is-panel-visible')
      })

      this.list.addEventListener('focusout', () => {
        this.classList.remove('is-panel-visible')
      })

      this.prevButton?.addEventListener('click', () => this.scrollBy(-1))
      this.nextButton?.addEventListener('click', () => this.scrollBy(1))
    }

    scrollBy(direction) {
      const items = [...this.querySelectorAll('[data-rv-item]')]
      if (!items.length) return

      this.scrollIndex = Math.max(
        0,
        Math.min(items.length - 1, this.scrollIndex + direction)
      )
      items[this.scrollIndex].scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      })
      this.updateButtons()
    }

    updateButtons() {
      const items = [...this.querySelectorAll('[data-rv-item]')]
      const hasOverflow = items.length > 3

      this.toggleAttribute('data-has-controls', hasOverflow)
      if (this.prevButton) this.prevButton.disabled = this.scrollIndex === 0
      if (this.nextButton) {
        this.nextButton.disabled =
          !hasOverflow || this.scrollIndex >= items.length - 1
      }
    }
  }

  window.RecentlyViewedSidebarTest = {
    addHandle,
    normalizeProductData,
    parseHandles,
    parseProductJson,
    renderProductItem,
    selectProductsByHandles,
    serializeHandles,
    upsertProductData,
  }

  if (!customElements.get('recently-viewed-sidebar')) {
    customElements.define('recently-viewed-sidebar', RecentlyViewedSidebar)
  }
})()
